import type { InvitationEmailOutcome, InvitationView } from "@kwp/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, ErrorText, Section, StatusText } from "../../ui";
import { OutcomeLine, type InvitationSender } from "./invite-sender";

/**
 * Settings → Workspaces → Invitations (WP-027): everyone invited, the workspace they were invited
 * to ("No workspace" for invites from before WP-028), whether the invitation was
 * emailed (and from where) and whether they have signed up; unaccepted invitations can be emailed
 * again from the mailbox chosen in "Invite a person".
 */
export function PendingInvitations({ sendFrom, send }: { sendFrom: string | null; send: InvitationSender }) {
  const trpc = useTRPC();
  const invitations = useQuery(trpc.admin.invitations.queryOptions());
  const [done, setDone] = useState<{ email: string; outcome: InvitationEmailOutcome } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const rows: readonly InvitationView[] = invitations.data === undefined ? [] : invitations.data;

  async function resend(email: string) {
    if (sendFrom === null) return;
    setDone(null);
    setFailure(null);
    try {
      const outcome = await send.resend(email, sendFrom);
      if (outcome === "cancelled") setFailure("Not sent: confirm with your passkey to email the invitation.");
      else setDone({ email, outcome });
    } catch (err) {
      setFailure(errorMessage(err));
    }
  }

  return (
    <Section title="Invitations" description="People you invited, to which workspace, whether the invitation was emailed, and whether they have signed up.">
      {invitations.isPending ? <StatusText>Loading invitations…</StatusText> : null}
      {invitations.isError ? <ErrorText>{errorMessage(invitations.error)}</ErrorText> : null}
      {invitations.isSuccess && rows.length === 0 ? <p className="text-sm text-muted">Nobody has been invited yet.</p> : null}
      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted">
              <tr>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Email
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Name
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Workspace
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Invitation email
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Signed up
                </th>
                <th scope="col" className="py-2 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.email} className="border-t border-border">
                  <td className="py-2 pr-4">{i.email}</td>
                  <td className="py-2 pr-4">{i.name}</td>
                  <td className="py-2 pr-4 text-muted">{i.workspaceName === null ? "No workspace" : i.workspaceName}</td>
                  <td className="py-2 pr-4 text-muted">{i.emailedFrom === null ? "Not emailed" : `Emailed from ${i.emailedFrom}`}</td>
                  <td className="py-2 pr-4 text-muted">{i.acceptedAt === null ? "Not yet" : "Yes"}</td>
                  <td className="py-2">
                    {i.acceptedAt === null && sendFrom !== null ? (
                      <Button aria-label={`Resend invitation to ${i.email}`} disabled={send.pending} onClick={() => void resend(i.email)}>
                        Resend invitation
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <div className="mt-2 min-h-5">
        {failure === null ? null : <ErrorText>{failure}</ErrorText>}
        {done === null ? null : <OutcomeLine email={done.email} outcome={done.outcome} invited={null} busy={send.pending} onResend={() => void resend(done.email)} />}
      </div>
    </Section>
  );
}
